import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import api from "../../api/client";
import NavIcon from "./shell/NavIcon";
import "./TopNavbar.css";

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

function colorFromTenant(tenantId) {
  const h = ((hashString(tenantId) % 360) + 360) % 360;
  return `hsl(${h}, 60%, 70%)`;
}

/**
 * The app bar every module shares: menu button, company logo, the module's
 * name, and the user menu. A module supplies what is its own —
 *
 *   subtitle     the module's name under the company name
 *   homePath     where the logo goes
 *   rightSlot    controls beside the user menu (PLM's Alerts)
 *   menuItems    extra user-menu entries: [{ label, onClick, disabled?, hidden? }]
 */
export default function TopNavbar({
  subStatus,
  sidebarOpen,
  sidebarId,
  onToggleSidebar,
  subtitle,
  homePath,
  rightSlot,
  menuItems = [],
}) {
  const { user, logout, tenantModule } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const dropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const dropdownId = "top-navbar-user-menu";

  const firstName = user?.firstName || "";
  const lastName = user?.lastName || "";
  const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
  const fallbackInitial = user?.email ? user.email[0].toUpperCase() : "U";
  const avatarLabel = initials || fallbackInitial;
  const tenantColor = colorFromTenant(user?.tenantId || "default-tenant");
  const fullName = `${firstName} ${lastName}`.trim() || user?.email || "User";
  const roleLabel =
    user?.roles?.length > 0
      ? user.roles
          .map((role) => role.name)
          .filter(Boolean)
          .join(", ")
      : user?.role?.name || "Role";

  // Close menu when route changes (including via browser back/forward)
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        profileButtonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!user?.tenantId) return;
    api
      .get("/tenant")
      .then((resp) => setTenantInfo(resp.data || null))
      .catch(() => {
        // ignore tenant info failures and use fallback label/avatar
      });
  }, [user?.tenantId]);

  const go = (path) => {
    navigate(path);
    setOpen(false);
  };

  const onLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const extraItems = menuItems.filter((item) => !item.hidden);

  return (
    <header className="ts-navbar ts-navbar--app">
      <div className="ts-navbar-inner">
        {onToggleSidebar && (
          <button
            type="button"
            className="ts-sidebar-toggle"
            onClick={onToggleSidebar}
            aria-label={sidebarOpen ? "Hide navigation" : "Show navigation"}
            aria-expanded={sidebarOpen}
            aria-controls={sidebarId}
            title={sidebarOpen ? "Hide navigation" : "Show navigation"}
          >
            <NavIcon name="menu" className="ts-sidebar-toggle-icon" />
          </button>
        )}
        <Link to={homePath} className="ts-nav-logo">
          <span className="ts-logo-mark">
            {tenantInfo?.customImageFileName ? (
              <img
                src={`${process.env.REACT_APP_API_UPLOAD_URL}${tenantInfo.customImageFileName}`}
                className="ts-nav-logo-img"
                alt={tenantInfo?.name || "Tenant"}
              />
            ) : (
              <span className="ts-logo-fallback">
                {(tenantInfo?.name || "T")[0]?.toUpperCase() || "T"}
              </span>
            )}
          </span>
          <span className="ts-logo-text">
            <span className="ts-logo-name">
              {tenantInfo?.name || "DecisionFlow"}
            </span>
            {subtitle && <span className="ts-logo-subtitle">{subtitle}</span>}
          </span>
        </Link>
        <div className="ts-navbar-right">
          {rightSlot}
          <div className="ts-user-wrap" ref={dropdownRef}>
            <button
              type="button"
              ref={profileButtonRef}
              className="ts-user-trigger"
              onClick={() => setOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-controls={dropdownId}
              title="Open user menu"
            >
              <span
                className="ts-profile-circle"
                style={{ backgroundColor: tenantColor }}
                aria-hidden="true"
              >
                {avatarLabel}
              </span>
              <span className="ts-user-meta">
                <span className="ts-user-name">{fullName}</span>
                <span className="ts-user-tenant">
                  {user?.tenantName || "Tenant"}
                </span>
                <span className="ts-user-role">{roleLabel}</span>
              </span>
            </button>
            {open && (
              <div id={dropdownId} className="ts-profile-dropdown" role="menu">
                <div className="ts-dropdown-header">
                  <strong>
                    {firstName} {lastName}
                  </strong>
                  <div className="flex justify-between">
                    <span className="ts-tenant-name">
                      {user?.tenantName || "Tenant"}
                    </span>
                    {" - "}
                    <span className="ts-tenant-name">{roleLabel}</span>
                  </div>
                </div>
                <div className="ts-dropdown-divider" />
                <button className="ts-dropdown-item" type="button" onClick={() => go("/change-password")}>
                  Change Password
                </button>
                {/* Admins have AI Access in the Admin sidebar instead. */}
                {tenantModule === "dms" && !user?.isAdmin && (
                  <button className="ts-dropdown-item" type="button" onClick={() => go("/ai-access")}>
                    AI Access
                  </button>
                )}
                {user?.isAdmin && (
                  <button className="ts-dropdown-item" type="button" onClick={() => go("/subscription")}>
                    Manage Subscription
                    {subStatus?.warningLevel &&
                      subStatus.warningLevel !== "expired" && (
                        <span
                          style={{
                            marginLeft: 6,
                            padding: "1px 6px",
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 700,
                            background:
                              subStatus.warningLevel === "critical"
                                ? "#fee2e2"
                                : "#fef9c3",
                            color:
                              subStatus.warningLevel === "critical"
                                ? "#be123c"
                                : "#854d0e",
                          }}
                        >
                          {subStatus.daysRemaining}d left
                        </span>
                      )}
                  </button>
                )}
                {extraItems.map((item) => (
                  <button
                    key={item.label}
                    className="ts-dropdown-item"
                    type="button"
                    disabled={item.disabled}
                    onClick={() => {
                      setOpen(false);
                      item.onClick();
                    }}
                  >
                    {item.label}
                  </button>
                ))}
                <div className="ts-dropdown-divider" />
                <button
                  className="ts-dropdown-item danger"
                  type="button"
                  onClick={onLogout}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
